import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import apiMiddleware from "../../api/apiMiddleware";
import Toster from "../common/Toster";
import { AdminPageLayout, AdminPageHeader } from "../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../common/supplierPageMeta";
import { getServicesFromUser, syncSupplierUser } from "../../utils/supplierServices";
import ProfileBankDetails from "./ProfileBankDetails";

const Profile = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  // Get user data from localStorage
  const userData = useMemo(() => {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) return null;
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }, []);

  // Get service provided
  const getServicesProvided = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) return [];
      return getServicesFromUser(JSON.parse(userStr));
    } catch (error) {
      console.error("Error parsing service_provided:", error);
      return [];
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    name: userData?.name || "",
    email: userData?.email || "",
    phone: userData?.phone || "",
    alternate_phone: userData?.alternate_phone || "",
    company_name: userData?.supplier_details?.company_name || userData?.supplierDetails?.company_name || "",
    company_website: userData?.supplier_details?.company_website || userData?.supplierDetails?.company_website || "",
    company_phone: userData?.supplier_details?.company_phone || userData?.supplierDetails?.company_phone || "",
    company_registration_number: userData?.supplier_details?.company_registration_number || userData?.supplierDetails?.company_registration_number || "",
    service_provided: getServicesProvided(),
  });

  // Fetch profile data
  const { data: profileData } = useQuery({
    queryKey: ["supplierProfile"],
    queryFn: async () => {
      const response = await apiMiddleware.get("/supplier/profile");
      return response.data;
    },
    enabled: !!userData,
  });


  // Get profile image and CIN document URLs
  const profileImageUrl = useMemo(() => {
    if (!userData) return null;
    const profileImage =
      userData?.supplier_details?.profile_image_url ||
      userData?.supplierDetails?.profile_image_url ||
      userData?.supplier_details?.profile_image ||
      userData?.supplierDetails?.profile_image;
    if (profileImage && !profileImage.startsWith("http")) {
      return `${process.env.REACT_APP_API_URL}/${profileImage}`;
    }
    return profileImage;
  }, [userData]);

  const cinDocumentUrl = useMemo(() => {
    if (!userData) return null;
    const cinDoc =
      userData?.supplier_details?.cin_registration_form_url ||
      userData?.supplierDetails?.cin_registration_form_url ||
      userData?.supplier_details?.cin_registration_form ||
      userData?.supplierDetails?.cin_registration_form;
    if (cinDoc && !cinDoc.startsWith("http")) {
      return `${process.env.REACT_APP_API_URL}/${cinDoc}`;
    }
    return cinDoc;
  }, [userData]);

  // Update form data when profile data is fetched
  useEffect(() => {
    if (profileData?.data?.user) {
      const user = profileData.data.user;
      const services = getServicesFromUser(user);

      syncSupplierUser(user);

      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        alternate_phone: user.alternate_phone || "",
        company_name: user.supplier_details?.company_name || user.supplierDetails?.company_name || "",
        company_website: user.supplier_details?.company_website || user.supplierDetails?.company_website || "",
        company_phone: user.supplier_details?.company_phone || user.supplierDetails?.company_phone || "",
        company_registration_number: user.supplier_details?.company_registration_number || user.supplierDetails?.company_registration_number || "",
        service_provided: services,
      });
    }
  }, [profileData]);

  // Update mutation
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiMiddleware.put("/supplier/profile", data);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.data?.user) {
        syncSupplierUser(data.data.user);
      }
      setShowSuccess(true);
      setSuccessMessage("Profile updated successfully");
      queryClient.invalidateQueries(["supplierProfile"]);
    },
    onError: (error) => {
      setShowError(true);
      setErrorMessage(error.response?.data?.message || "Failed to update profile");
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleServiceChange = async (selectedOptions) => {
    const newServices = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    
    // Prevent removing the last service
    if (newServices.length === 0 && formData.service_provided.length > 0) {
      setShowError(true);
      setErrorMessage("At least one service is required. You cannot remove all services.");
      return;
    }
    
    // Check if any services are being removed
    const removedServices = formData.service_provided.filter(
      (service) => !newServices.includes(service)
    );
    
    // If services are being removed, validate with backend
    if (removedServices.length > 0) {
      try {
        // Create a temporary data object to check validation
        const checkData = {
          ...formData,
          service_provided: JSON.stringify(newServices),
        };
        
        // Try to validate by making a test request
        // We'll let the backend handle the validation on submit
        // For now, just allow the change and show error on submit if needed
      } catch (error) {
        // Error will be handled on submit
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      service_provided: newServices,
    }));
    
    // Clear any previous service-related error
    if (errorMessage && errorMessage.includes("At least one service is required")) {
      setShowError(false);
      setErrorMessage("");
    }
  };

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiMiddleware.post("/auth/change-password", {
        old_password: data.old_password,
        new_password: data.new_password,
      });
      return response.data;
    },
    onSuccess: () => {
      setShowSuccess(true);
      setSuccessMessage("Password changed successfully");
      setPasswordData({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });
      setShowPasswordSection(false);
    },
    onError: (error) => {
      setShowError(true);
      setErrorMessage(error.response?.data?.message || "Failed to change password");
    },
  });

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setShowError(true);
      setErrorMessage("New passwords do not match");
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 8) {
      setShowError(true);
      setErrorMessage("New password must be at least 8 characters long");
      return;
    }

    changePasswordMutation.mutate(passwordData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that at least one service is selected
    if (!formData.service_provided || formData.service_provided.length === 0) {
      setShowError(true);
      setErrorMessage("At least one service is required. Please select at least one service.");
      return;
    }
    
    const dataToSend = {
      ...formData,
      service_provided: JSON.stringify(formData.service_provided),
    };
    updateMutation.mutate(dataToSend);
  };

  const pageMeta = SUPPLIER_PAGE_META.profile;

  if (!userData) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <div className="rounded-lg bg-white p-6 text-center shadow">
          <p className="text-gray-600">Unable to load profile data. Please try again.</p>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      {/* Error Toster */}
      {showError && errorMessage && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => {
            setShowError(false);
            setErrorMessage("");
          }}
        />
      )}

      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              type="button"
              onClick={() => setActiveTab("profile")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "profile"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Profile Information
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("bank")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "bank"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Bank Details
            </button>
          </nav>
        </div>
      </div>

      {/* Profile Form */}
      {activeTab === "profile" && (
      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information Section */}
            <div className="md:col-span-2">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Personal Information
              </h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alternate Number
              </label>
              <input
                type="tel"
                name="alternate_phone"
                value={formData.alternate_phone}
                onChange={handleChange}
                placeholder="Optional secondary contact number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Company Information Section */}
            <div className="md:col-span-2 mt-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Company Information
              </h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Phone
              </label>
              <input
                type="tel"
                name="company_phone"
                value={formData.company_phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Website
              </label>
              <input
                type="url"
                name="company_website"
                value={formData.company_website}
                onChange={handleChange}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Registration Number (CIN)
              </label>
              <input
                type="text"
                name="company_registration_number"
                value={formData.company_registration_number}
                onChange={handleChange}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">CIN cannot be changed. Please contact admin for updates.</p>
            </div>
          </div>

          {/* Services Provided Section */}
          <div className="md:col-span-2 mt-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              Services Provided
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Services You Provide
              </label>
              <Select
                isMulti
                value={formData.service_provided.map((service) => ({
                  value: service,
                  label: service.charAt(0).toUpperCase() + service.slice(1),
                }))}
                onChange={handleServiceChange}
                options={[
                  { value: "package", label: "Package" },
                  { value: "activity", label: "Activity" },
                  { value: "attraction", label: "Attraction" },
                  { value: "rental", label: "Rental" },
                  { value: "event", label: "Event" },
                ]}
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Select services you provide"
                isClearable={false}
                closeMenuOnSelect={false}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "#ec4899",
                    primary75: "#f9a8d4",
                    primary50: "#fce7f3",
                    primary25: "#fdf2f8",
                  },
                })}
                styles={{
                  multiValueRemove: (base, state) => {
                    // Disable remove button if it's the last service
                    if (formData.service_provided.length === 1) {
                      return {
                        ...base,
                        cursor: 'not-allowed',
                        opacity: 0.5,
                      };
                    }
                    return base;
                  },
                }}
              />
              <p className="text-xs text-gray-500 mt-2">
                You can add or remove services. At least one service is required. Changes will be saved when you click "Save Changes".
              </p>
            </div>
          </div>

          {/* Documents Section */}
          <div className="md:col-span-2 mt-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              Documents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Image
                </label>
                {profileImageUrl ? (
                  <div className="space-y-2">
                    <img
                      src={profileImageUrl}
                      alt="Profile"
                      className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                    />
                    <a
                      href={profileImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <i className="fi fi-rr-external-link"></i>
                      View Full Image
                    </a>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No profile image uploaded</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CIN Registration Document
                </label>
                {cinDocumentUrl ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-300">
                      <i className="fi fi-rr-file text-2xl text-gray-600"></i>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">CIN Document</p>
                        <p className="text-xs text-gray-500">Registration document</p>
                      </div>
                    </div>
                    <a
                      href={cinDocumentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <i className="fi fi-rr-external-link"></i>
                      View Document
                    </a>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No CIN document uploaded</p>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Documents cannot be changed. Please contact admin for updates.
            </p>
          </div>

          {/* Submit Button */}
          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
      )}

      {activeTab === "bank" && (
        <div className="bg-white rounded-lg shadow">
          <ProfileBankDetails
            profileData={profileData}
            onSuccess={(message) => {
              setShowSuccess(true);
              setSuccessMessage(message);
            }}
            onError={(message) => {
              setShowError(true);
              setErrorMessage(message);
            }}
          />
        </div>
      )}

      {/* Change Password Section */}
      {activeTab === "profile" && (
      <div className="bg-white rounded-lg shadow mt-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Change Password</h2>
              <p className="text-sm text-gray-600 mt-1">Update your account password</p>
            </div>
            <button
              onClick={() => {
                setShowPasswordSection(!showPasswordSection);
                if (showPasswordSection) {
                  setPasswordData({
                    old_password: "",
                    new_password: "",
                    confirm_password: "",
                  });
                  setShowError(false);
                  setErrorMessage("");
                }
              }}
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
            >
              {showPasswordSection ? "Cancel" : "Change Password"}
            </button>
          </div>

          {showPasswordSection && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.old ? "text" : "password"}
                    name="old_password"
                    value={passwordData.old_password}
                    onChange={handlePasswordChange}
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords((prev) => ({ ...prev, old: !prev.old }))}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <i className={`fi ${showPasswords.old ? "fi-rr-eye-crossed" : "fi-rr-eye"}`}></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    placeholder="Enter new password (min 8 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords((prev) => ({ ...prev, new: !prev.new }))}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <i className={`fi ${showPasswords.new ? "fi-rr-eye-crossed" : "fi-rr-eye"}`}></i>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords((prev) => ({ ...prev, confirm: !prev.confirm }))}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <i className={`fi ${showPasswords.confirm ? "fi-rr-eye-crossed" : "fi-rr-eye"}`}></i>
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={changePasswordMutation.isPending}
                  className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {changePasswordMutation.isPending ? "Changing..." : "Change Password"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      )}
    </AdminPageLayout>
  );
};

export default Profile;

